import React, { useState } from 'react';
import { useTranslation } from 'react-i18next';

import { ReactComponent as CaffeeInActive } from '../../../assets/icons/amenities/caffee-inactive.svg';
import { ReactComponent as CarwashInActive } from '../../../assets/icons/amenities/carwash-inactive.svg';
import { ReactComponent as GasStationInActive } from '../../../assets/icons/amenities/gas-station-inactive.svg';
import { ReactComponent as LockerInActive } from '../../../assets/icons/amenities/locker-inactive.svg';
import { ReactComponent as LoungeInActive } from '../../../assets/icons/amenities/lounge-inactive.svg';
import { ReactComponent as RelaxingInActive } from '../../../assets/icons/amenities/relaxing-inactive.svg';
import { ReactComponent as RestaurantInActive } from '../../../assets/icons/amenities/restaurant-inactive.svg';
import { ReactComponent as RestroomInActive } from '../../../assets/icons/amenities/restroom-inactive.svg';
import { ReactComponent as ShoppingInActive } from '../../../assets/icons/amenities/shopping-inactive.svg';
import { ReactComponent as WifiInActive } from '../../../assets/icons/amenities/wifi-inactive.svg';
import { ReactComponent as WorkshopInActive } from '../../../assets/icons/amenities/workshop-inactive.svg';
import { ReactComponent as RemoveIcon } from '../../../assets/icons/remove-icon.svg';
import { useAppBranding } from '../../../config/branding';
import { Amenity } from '../../../interface/station-management.interface';
import MyButton from '../../../shared-components/button/Button';

interface SelectedAmenitiesProperties {
  selectedAmenities: Amenity[];
  handleCancel: () => void;
  handleStationDetailChange: (name: string, changeValue: Amenity[]) => void;
  hideButtons?: boolean;
  hideRemove?: boolean;
}

interface AmenitiesDetailsProperties {
  name: string;
  key: Amenity;
  svgIconActive: JSX.Element;
  svgIconInActive: JSX.Element;
}

/**
 * Selected amenities popup
 */
function SelectAmenities(properties: SelectedAmenitiesProperties) {
  const branding = useAppBranding();
  const { selectedAmenities, handleCancel, handleStationDetailChange, hideButtons, hideRemove } =
    properties;
  const { t } = useTranslation('stationManagement');

  const [newlySelectedAmenities, setNewlySelectedAmenities] =
    useState<Amenity[]>(selectedAmenities);

  const amenitiesDetails: AmenitiesDetailsProperties[] = [
    {
      name: t('gasStation'),
      key: 'Gas Station',
      svgIconActive: <branding.icons.GasStationActive />,
      svgIconInActive: <GasStationInActive />,
    },
    {
      name: t('wifi'),
      key: 'Free Wifi',
      svgIconActive: <branding.icons.WifiActive />,
      svgIconInActive: <WifiInActive />,
    },
    {
      name: t('restroom'),
      key: 'Restroom',
      svgIconActive: <branding.icons.RestroomActive />,
      svgIconInActive: <RestroomInActive />,
    },
    {
      name: t('Lounge'),
      key: 'Lounge',
      svgIconActive: <branding.icons.LoungeActive />,
      svgIconInActive: <LoungeInActive />,
    },
    {
      name: t('cafe'),
      key: 'Cafe',
      svgIconActive: <branding.icons.CaffeeActive />,
      svgIconInActive: <CaffeeInActive />,
    },
    {
      name: t('shopping'),
      key: 'Shopping',
      svgIconActive: <branding.icons.ShoppingActive />,
      svgIconInActive: <ShoppingInActive />,
    },
    {
      name: t('restaurant'),
      key: 'Restaurant',
      svgIconActive: <branding.icons.RestaurantActive />,
      svgIconInActive: <RestaurantInActive />,
    },
    {
      name: t('relaxingArea'),
      key: 'Relaxing Area',
      svgIconActive: <branding.icons.RelaxingActive />,
      svgIconInActive: <RelaxingInActive />,
    },
    {
      name: t('locker'),
      key: 'Locker',
      svgIconActive: <branding.icons.LockerActive />,
      svgIconInActive: <LockerInActive />,
    },
    {
      name: t('workshop'),
      key: 'Work Shop',
      svgIconActive: <branding.icons.WorkshopActive />,
      svgIconInActive: <WorkshopInActive />,
    },
    {
      name: t('carWash'),
      key: 'Car Wash',
      svgIconActive: <branding.icons.CarwashActive />,
      svgIconInActive: <CarwashInActive />,
    },
  ];

  /**
   * Toggle amenities selection
   */
  const toggleAmenitiesSelection = (amenity: Amenity) => {
    if (!hideButtons) {
      setNewlySelectedAmenities((previousAmenities) => {
        if (previousAmenities?.includes(amenity)) {
          // Remove amenity from the array
          return previousAmenities.filter((each) => each !== amenity);
        }
        // Add amenity to the array
        return [...previousAmenities, amenity];
      });
    }
  };

  /**
   * Send selected amenities
   */
  const handleSubmit = () => {
    handleStationDetailChange('amenities', newlySelectedAmenities);
    handleCancel();
  };

  return (
    <div className="select-amenities-wrap ">
      <div className="select-amenities-container versionV1">
        {amenitiesDetails?.map((amenity) => (
          <div
            className="amenity-wrap"
            key={amenity.key}
            role="presentation"
            onClick={
              !newlySelectedAmenities?.includes(amenity.key)
                ? () => toggleAmenitiesSelection(amenity.key)
                : () => {}
            }>
            <div className="amenity-icon">
              <span
                className={`active-icon ${newlySelectedAmenities?.includes(amenity.key) ? 'view' : 'hide'}`}>
                {amenity.svgIconActive}
              </span>
              <span
                className={`inactive-icon ${!newlySelectedAmenities?.includes(amenity.key) ? 'view' : 'hide'}`}>
                {amenity.svgIconInActive}
              </span>
              {!hideRemove && newlySelectedAmenities?.includes(amenity.key) && (
                <div
                  className="remove-icon"
                  role="presentation"
                  onClick={() => toggleAmenitiesSelection(amenity.key)}>
                  <RemoveIcon />
                </div>
              )}
            </div>
            <p>{amenity.name}</p>
          </div>
        ))}
      </div>
      {!hideButtons && (
        <div className="select-amenities-button-wrap">
          <MyButton label={t('cancel')} buttonType="back" onClickFunc={handleCancel} />
          <MyButton label={t('submit')} buttonType="submit" onClickFunc={handleSubmit} />
        </div>
      )}
    </div>
  );
}

export default SelectAmenities;
